import styled from "styled-components"
import { FC, useEffect, useMemo, useState } from "react"
import ReactDropdownImproved from "../../common/ReactDropdownImproved"
import { inGamePositions } from "../../../content-options/positions/inGamePositions"
import { Team, UpdateTeamDTO, LineupPositionDTO } from "../../../models/team"
import { useLeague } from "../../../context/useLeague"
import { User } from "../../../models/user"
import TeamService from "../../../services/team"
import useNotyf from "../../../context/useNotify"
import Dropdown from "../../common/Dropdown"
import { Fixture } from "../../../models/fixture"
import { UpdateFixturesDTO } from "../../../models/league"
import helperService from "../../../services/helper"
import FixtureService from '../../../services/fixture'

const SelectPlayers: FC = ({}) => {
  const { userTeam, lineup, setLineup, league, getLeagueById } = useLeague()
  const notyf = useNotyf()

  // data
  const [positionOptions, setPositionOptions] = useState<string[]>([])
  const [playerOptions, setPlayerOptions] = useState<User[]>([])
  const [playerSelected, setPlayerSelected] = useState<User | null>(null)
  const [positionSelected, setPositionSelected] = useState<string>("")
  const [anySelected, setAnySelected] = useState<User | null>(null)
  const [selectedFixture, setSelectedFixture] = useState<Fixture>()

  // API
  const updateTeam = async (liveLink: string, image: string, lineup: LineupPositionDTO[]) => {
    try {
      if (!userTeam || !league) {
        return
      }
      const dto: UpdateTeamDTO = { liveLink, image, lineup }
      await TeamService.updateTeam(userTeam._id, dto);
      notyf.success("Successfully updated the lineup for your team.")
      getLeagueById(league._id);
    } catch (error) {
      console.log("Error updating lineup: ", error)
      helperService.showNotfyErr(notyf, error, "An error occurred while updating the lineup. Please try again.")
    }
  }


  const handleSetLineup = async () => {
    if (!userTeam) {
      return
    }
    const lineupDTO: LineupPositionDTO[] = lineup.map((el) => ({
      user: el.user._id,
      position: el.position,
      isAny: el.isAny,
    }))
    updateTeam(userTeam.liveLink, userTeam.image, lineupDTO)
  }

    const updateFixture = async () => {
      try {
        if (!selectedFixture) {
          return
        }
        const isHomeTeam = selectedFixture.home === userTeam?.name;
        
        const dto: UpdateFixturesDTO = {
          awayScore: selectedFixture.awayScore,
          homeScore: selectedFixture.homeScore,
          lineupAway: selectedFixture.lineupAway,
          lineupAwayAny: selectedFixture.lineupAwayAny,
          lineupHome: selectedFixture.lineupHome,
          lineupHomeAny: selectedFixture.lineupHomeAny,
          isPlayed: selectedFixture.isPlayed,
          isDisputed: selectedFixture.isDisputed,
        }
        if (isHomeTeam) {
          dto.lineupHome = lineup
        } else {
          dto.lineupAway = lineup
        }
        await FixtureService.updateFixture(selectedFixture._id, dto)
        notyf.success("Successfully updated the lineup for your team.")
        handleRefreshLeague()
    } catch (err) {
        console.log("Error updating fixture: ", err)
        helperService.showNotfyErr(notyf, err, "An error occurred while updating the fixture. Please try again.")
    }
  }
  
  const handleRefreshLeague = () => {
    if (!league) {
        return
    }
    getLeagueById(league?._id);
  };

  // Dropdown Options
  const getPlayerOptions = () => {
    const players = userTeam?.roster
    const lineupPlayers = lineup.map((el) => el.user)
    const anyPlayer = lineup.find(el => el.position === 'Any')?.user
    const filteredPlayers =
      players?.filter(
        (player) =>
          player._id !== playerSelected?._id &&
          player._id !== anySelected?._id &&
          !lineupPlayers.map((el) => el._id).includes(player._id)
      ) || []
    if (anyPlayer) {
      filteredPlayers.unshift({...anyPlayer, username: 'Any'})
    }
    return filteredPlayers
  }

  const getPositionOptions = () => {
    const positions = inGamePositions
    const lineupPositions = lineup.map((el) => el.position)
    const anyPlayer = lineup.find(el => el.position === 'Any')?.user
    const options = positions.filter(
      (position) =>
        position !== positionSelected && !lineupPositions?.includes(position)
    )
    if (!anyPlayer) {
      options.unshift("Any")
    }
      
    return options
  }

  const getFixtureOptions = useMemo(() => {
    if (!league) return []
    return league.fixtures
      .filter(fixture => !fixture.isLocked)
      .filter(fixture => fixture.home === userTeam?.name || fixture.away === userTeam?.name)
      .map(fixture => ({
        label: `${fixture.home} vs ${fixture.away} - Week ${fixture.week}, ${fixture.date}, ${fixture.time}`,
        value: fixture
    }));
  }, [league]);

  // Helpers
  const getSelectedUser = (username: string) => {
    const players = userTeam?.roster
    return players?.find((user) => user.username === username) || null
  }

  const addToLineup = (user: User, position: string, isAny: boolean) => {
    // unique position
    if (lineup.map(el => el.position).includes(position)) {
      return
    }
    if (lineup.filter(el => el.position !== 'Any' ).length < 11) {
      setLineup([...lineup, { user, position, isAny }])
    } else {
      notyf.error("Cannot add more than 11 players")
    }
  }

  // handlers
  const handleSelectPlayer = (options: any) => {
    const username = options.label
    const anyPlayer = lineup.find(el => el.position === 'Any')?.user
    if (username === 'Any' && anyPlayer) {
      setPlayerSelected({...anyPlayer, username: 'Any'})
      return
    }

    if (!username) {
      return
    }
    const selectedUser = getSelectedUser(username)
    setPlayerSelected(selectedUser)
  }

  const handleSelectPosition = (options: any) => {
    const position = options.label
    setPositionSelected(position)
  }

  const handleSelectFixture = (selectedOption: any) => {
    if (!selectedOption) {
      return
    }
    setSelectedFixture(selectedOption.value)
  }

  const handleClickAdd = () => {
    const anyPlayer = lineup.find(el => el.position === 'Any')?.user
    const isAddingAny = playerSelected?.username === 'Any'
    if (anyPlayer && isAddingAny) {
      addToLineup(anyPlayer, positionSelected, true)
      return
    }

    if (lineup.some((el) => el.user._id === playerSelected?._id)) {
      return
    }
    if (playerSelected && positionSelected) {
      addToLineup(playerSelected, positionSelected, false)
    }
  }

  const handleClickClearLineup = () => {
    setLineup([])
    setAnySelected(null)
    setPlayerSelected(null)
    setPositionSelected("")

    const players = getPlayerOptions()
    setPlayerOptions(players)

    const positions = getPositionOptions()
    setPositionOptions(positions)
  }

  const handleClickSubmitLineup = () => {
    updateFixture()
  }

  // Use effects
  useEffect(() => {
    const players = getPlayerOptions()
    setPlayerOptions(players)
    const positions = getPositionOptions()
    setPositionOptions(positions)
  }, [])

  useEffect(() => {
    setPlayerOptions(getPlayerOptions())
    setPositionOptions(getPositionOptions())
  }, [playerSelected, positionSelected, anySelected, lineup])

  return (
    <Container className="grey-box h-100 gradient-gray">
      <div className="d-flex justify-content-center align-items-center flex-column h-100">
        <div className=" mt-sm-3 mt-0">
          <h4 className="text-center line-up-header heading fw-bold">
            Manage Lineup
          </h4>
        </div>
        {/* dropdowns */}
        <form className=" d-flex flex-column">
          <div
            className="form-group mt-sm-3"
            style={{ maxWidth: "250px", width: "100%" }}
          >
            <label>Select player</label>
            <ReactDropdownImproved
              text={"Select player..."}
              func={handleSelectPlayer}
              options={playerOptions.map((el) => el.username)}
            />
          </div>
          <div
            className="form-group mt-1"
            style={{ maxWidth: "250px", width: "100%" }}
          >
            <label>Select position</label>
            <ReactDropdownImproved
              text={"Select position..."}
              func={handleSelectPosition}
              options={positionOptions}
            />
          </div>
          {/* buttons */}
          <div
            className="form-group mt-1 mt-sm-4 mt-2 d-flex align-items-center justify-content-center"
            style={{ maxWidth: "250px", width: "100%" }}
          >
            <button
              type="button"
              onClick={handleClickAdd}
              className="btn btn-light btn-md me-1"
              style={{ width: "100px" }}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={handleClickClearLineup}
              className="btn btn-secondary btn-md ms-1"
              style={{ width: "100px" }}
            >
              CLEAR
            </button>
          </div>

          <div
            className="form-group mt-5 pt-2"
            style={{
              maxWidth: "250px",
              width: "100%",
              borderTop: "solid 2px white",
            }}
          >
            <label>
              Save line up for future use.
            </label>
            <div className=" d-flex align-items-center justify-content-end mt-2">
              <button
                type="button"
                onClick={handleSetLineup}
                className="btn btn-success btn-md me-2"
                style={{ maxWidth: "240px", width: "100%" }}
              >
                SAVE STARING XI
              </button>
            </div>
          </div>
          <div
            className="form-group mt-4 pt-2 "
            style={{
              maxWidth: "250px",
              width: "100%",
              borderTop: "solid 2px white",
            }}
          >
            <label className="pb-2">
              Ready to play? Submit your lineup to the official
              scorecard as soon as the fixture unlocks. Don't delay!
            </label>
            <label className="pb-1 mt-1">Select fixture</label>
            <Dropdown
                options={getFixtureOptions}
                onSelect={handleSelectFixture}
                text="Select League"
              />
            <div className="  d-flex align-items-center justify-content-end mt-2 mb-2">
              <button
                type="button"
                onClick={handleClickSubmitLineup}
                className="btn btn-success btn-md me-2"
                style={{ maxWidth: "140px", width: "100%" }}
              >
                SUBMIT
              </button>
            </div>
          </div>
        </form>
      </div>
    </Container>
  )
}

export default SelectPlayers

const Container = styled.div`
  height: 100%;
  border-radius: 8px;
  width: 100%;
  margin: 1rem;
  box-shadow: 0px 1px 5px rgb(38, 38, 38);
  border: 0.5px gray solid;

  @media (max-width: 991px) {
    height: auto !important;
    padding: 0.5rem !important;

    .form-group {
      margin-top: 0.25rem !important;
    }

    > div > div {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    h4 {
      margin-bottom: 0.5rem !important;
    }

    > div {
      
      justify-content: flex-start !important;
      padding-top: 0.5rem;
    }
  }

  @media (max-width: 576px) {
  height: auto !important;
    padding: 0.5rem !important;

    .form-group {
      margin-top: 0.25rem !important;
    }

    > div > div {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    h4 {
      margin-bottom: 0.5rem !important;
    }

    > div {
      
      justify-content: flex-start !important;
      padding-top: 0.5rem;
    }
  }
`
